/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from "react";
import { cn } from "@/shared/ui/utils/tailwind";

interface BadgeProps {
  children: React.ReactNode;
  variant?: "filled" | "outline" | "light" | "dot";
  size?: "sm" | "md" | "lg";
  color?: string;
  backgroundColor?: string;
  sx?: React.CSSProperties;
  className?: string;
  style?: React.CSSProperties;
}

const VARIANT_CLASSES: Record<string, string> = {
  filled: "",
  outline: "bg-transparent border border-current",
  light: "opacity-85",
  dot: "",
};

const SIZE_CLASSES: Record<string, string> = {
  sm: "px-2 h-5 text-xs",
  md: "px-2.5 h-6 text-sm",
  lg: "px-3 h-7 text-sm",
};

/**
 * Badge component that renders a styled inline label with variant support.
 *
 * @param props - Badge display properties including variant, color, and children.
 * @returns Badge React element.
 */
const Badge = ({
  children,
  variant = "filled",
  size = "sm",
  color = "var(--text-primary)",
  backgroundColor = "var(--surface-tertiary)",
  sx = {},
  className,
  style,
}: BadgeProps) => {
  const variantClass = VARIANT_CLASSES[variant] || "";
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.sm;
  const resolvedBg = variant === "outline" ? "transparent" : backgroundColor;
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center font-semibold leading-none",
        sizeClass,
        variantClass,
        className
      )}
      style={{
        borderRadius: '9999px',
        color,
        backgroundColor: resolvedBg,
        ...sx,
        ...style,
      }}
    >
      {variant === "dot" && (
        <span
          className="w-1.5 h-1.5 rounded-full mr-1"
          style={{ backgroundColor: color }}
        />
      )}
      {children}
    </span>
  );
};

export default Badge;
